import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEmployees, useCountries, useDepartments } from '@/lib/queries';
import { useAuthStore } from '@/store/auth.store';
import { initials } from '@isoft-people/utils';

const AVATAR_COLORS = [
  'from-primary to-primary-light',
  'from-accent to-green-400',
  'from-warning to-yellow-400',
  'from-error to-pink-400',
  'from-info to-cyan-400',
  'from-purple-500 to-indigo-400',
];

function statusBadge(status: string) {
  const map: Record<string, string> = {
    ACTIVE: 'bg-accent-light text-accent',
    INACTIVE: 'bg-gray-100 text-gray-600',
    ON_LEAVE: 'bg-yellow-50 text-warning',
    TERMINATED: 'bg-red-50 text-error',
  };
  const labels: Record<string, string> = {
    ACTIVE: 'Activo',
    INACTIVE: 'Inactivo',
    ON_LEAVE: 'En licencia',
    TERMINATED: 'Dado de baja',
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
        map[status] ?? 'bg-gray-100 text-gray-600'
      }`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {labels[status] ?? status}
    </span>
  );
}

export function EmployeesPage() {
  const role = useAuthStore((s) => s.user?.role);
  const canCreate = role === 'ADMIN' || role === 'HR';
  const [search, setSearch] = useState('');
  const [countryId, setCountryId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const { data, isLoading } = useEmployees({
    search: search || undefined,
    countryId: countryId || undefined,
    departmentId: departmentId || undefined,
    status: status || undefined,
    page,
    pageSize,
  });
  const { data: countries } = useCountries();
  const { data: departments } = useDepartments();

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startIdx = (page - 1) * pageSize + 1;
  const endIdx = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-font-primary">Colaboradores</h1>
          <p className="mt-1 text-sm text-font-secondary">
            Gestiona el talento humano de ISOFT
          </p>
        </div>
        {canCreate && (
          <Link
            to="/colaboradores/nuevo"
            className="flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-white shadow-sm hover:bg-primary-dark"
          >
            <Plus size={16} />
            Nuevo colaborador
          </Link>
        )}
      </div>

      <div className="rounded-lg border border-border bg-white shadow-sm">
        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-3 border-b border-border p-4">
          <select
            value={countryId}
            onChange={(e) => {
              setCountryId(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-md border border-border bg-white px-3 text-sm text-font-primary"
          >
            <option value="">País: Todos</option>
            {countries?.items?.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.nameEs}
              </option>
            ))}
          </select>
          <select
            value={departmentId}
            onChange={(e) => {
              setDepartmentId(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-md border border-border bg-white px-3 text-sm text-font-primary"
          >
            <option value="">Área: Todas</option>
            {departments?.items?.map((d: any) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-md border border-border bg-white px-3 text-sm text-font-primary"
          >
            <option value="">Estado: Todos</option>
            <option value="ACTIVE">Activo</option>
            <option value="INACTIVE">Inactivo</option>
            <option value="ON_LEAVE">En licencia</option>
            <option value="TERMINATED">Dado de baja</option>
          </select>

          <div className="ml-auto flex h-9 w-64 items-center gap-2 rounded-md border border-border px-3">
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Buscar..."
              className="flex-1 bg-transparent text-sm placeholder:text-font-tertiary focus:outline-none"
            />
          </div>
        </div>

        {/* Tabla */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[11px] font-semibold uppercase tracking-wide text-font-secondary">
                <th className="px-6 py-3">Nombre</th>
                <th className="px-4 py-3">Puesto</th>
                <th className="px-4 py-3">Área</th>
                <th className="px-4 py-3">País</th>
                <th className="px-4 py-3">Jefe</th>
                <th className="px-4 py-3">Estado</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-font-secondary">
                    Cargando...
                  </td>
                </tr>
              )}
              {data?.items?.map((e: any, i: number) => {
                const color = AVATAR_COLORS[i % AVATAR_COLORS.length];
                const fullName = `${e.firstName} ${e.lastName}`;
                return (
                  <tr
                    key={e.id}
                    className="border-b border-border-light transition hover:bg-bg"
                  >
                    <td className="px-6 py-4">
                      <Link to={`/colaboradores/${e.id}`} className="flex items-center gap-3">
                        <div
                          className={`flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br ${color} text-[11px] font-semibold text-white`}
                        >
                          {initials(fullName)}
                        </div>
                        <div>
                          <p className="font-semibold text-font-primary hover:text-primary">
                            {fullName}
                          </p>
                          <p className="text-xs text-font-tertiary">
                            {e.corporateEmail ?? e.employeeNumber}
                          </p>
                        </div>
                      </Link>
                    </td>
                    <td className="px-4 py-4 text-font-secondary">
                      {e.position?.title ?? '–'}
                    </td>
                    <td className="px-4 py-4 text-font-secondary">
                      {e.department?.name ?? '–'}
                    </td>
                    <td className="px-4 py-4 text-font-secondary">
                      {e.country?.code ?? '–'}
                    </td>
                    <td className="px-4 py-4 text-font-secondary">
                      {e.manager
                        ? `${e.manager.firstName} ${e.manager.lastName}`
                        : '–'}
                    </td>
                    <td className="px-4 py-4">{statusBadge(e.status)}</td>
                  </tr>
                );
              })}
              {!isLoading && data?.items?.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-font-secondary">
                    Sin resultados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        <div className="flex items-center justify-between border-t border-border px-6 py-3">
          <p className="text-xs text-font-secondary">
            Mostrando {total === 0 ? 0 : startIdx} a {endIdx} de {total} colaboradores
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-font-secondary hover:bg-bg disabled:opacity-40"
            >
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: Math.min(3, totalPages) }, (_, i) => i + 1).map(
              (p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`flex h-8 w-8 items-center justify-center rounded-md text-xs font-semibold ${
                    p === page
                      ? 'bg-primary text-white'
                      : 'border border-border text-font-secondary hover:bg-bg'
                  }`}
                >
                  {p}
                </button>
              ),
            )}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-font-secondary hover:bg-bg disabled:opacity-40"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
